import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, CalendarHeart } from "lucide-react";
import BrandLink from "../components/BrandLink";
import { useAuth } from "../lib/auth";
import { hostCtaTarget } from "../lib/hostProfile";
import DemoLandingSection from "../components/demo/DemoLandingSection";
import HomeFeatureMatrix from "../components/HomeFeatureMatrix";
import { getTodaysDemoEvent } from "../lib/demoExperience";
import { useDemoChooser } from "../lib/demoChooserContext";
import HostTypewriterLabel, { HOST_TYPEWRITER_ARIA_LABEL } from "../components/HostTypewriterLabel";
import { Button } from "../components/ui";
import { HostThemePreviewShell } from "../lib/hostThemePreview";

export default function HomePage() {
  const nav = useNavigate();
  const { account, checking } = useAuth();
  const signedIn = Boolean(account);
  const hostCta = hostCtaTarget(account);
  const { openDemoChooser } = useDemoChooser();
  const [demoEvent] = useState(() => getTodaysDemoEvent());

  useEffect(() => {
    if (!checking && account) nav("/events", { replace: true });
  }, [account, checking, nav]);

  if (checking || account) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <p className="font-display text-[18px] text-ink/70">Spinning the disco ball…</p>
      </div>
    );
  }

  return (
    <HostThemePreviewShell className="min-h-screen bg-canvas text-ink font-body home-landing">
      <section className="home-hero relative overflow-hidden">
        <div className="home-hero-wash absolute inset-0 pointer-events-none" aria-hidden />

        <header className="relative z-10 mx-auto w-full max-w-page lg:max-w-5xl px-4 lg:px-8 pt-5 pb-2 flex items-center justify-between gap-3">
          <BrandLink
            to="/"
            variant="sticker"
            size="sm"
            testId="brand-link"
            className="opacity-90"
            wordmarkClassName="font-normal text-[15px] text-white/80"
            aria-label="rsvp.dance home"
            cycleTheme
          >
            <span className="sr-only">rsvp.dance</span>
          </BrandLink>
          <div className="flex items-center gap-4 flex-wrap">
            {signedIn ? (
              <>
                <Link
                  to="/events"
                  className="inline-flex items-center gap-1.5 font-ui font-medium text-[13px] text-white/70 hover:text-white transition-colors"
                  data-testid="nav-feed-link"
                >
                  <CalendarHeart className="w-4 h-4" strokeWidth={2} /> My events
                </Link>
                <Link
                  to="/me"
                  className="inline-flex items-center gap-1.5 font-ui font-medium text-[13px] text-white/70 hover:text-white transition-colors"
                  data-testid="nav-profile-link"
                >
                  <User className="w-4 h-4" strokeWidth={2} /> Profile
                </Link>
              </>
            ) : (
              <Link
                to="/login"
                className="font-ui font-medium text-[13px] text-white/70 hover:text-white transition-colors"
                data-testid="nav-signin-link"
              >
                Sign in
              </Link>
            )}
          </div>
        </header>

        <div className="relative mx-auto w-full max-w-page lg:max-w-5xl px-4 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-20 lg:pt-16 lg:pb-24">
          <div className="home-hero-copy max-w-2xl">
            <p className="font-display text-[clamp(2.5rem,9.5vw,4rem)] font-medium leading-[1.02] tracking-[-0.02em] text-white text-balance">
              rsvp.dance
            </p>
            <h1 className="mt-5 font-display font-medium text-[clamp(1.45rem,4.5vw,2rem)] leading-[1.15] tracking-[-0.02em] text-white/95 text-balance">
              Simple event RSVPs for dance hosts.
            </h1>
            <p className="mt-4 max-w-md text-[15px] font-normal leading-relaxed text-white/80 text-pretty">
              Share quick links on WhatsApp, manage live guest lists, and pack your socials without the technical overhead.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="primary"
                size="lg"
                data-testid="hero-try-demo-btn"
                onClick={openDemoChooser}
              >
                Try tonight&apos;s demo →
              </Button>
              <Button
                type="button"
                variant="heroSecondary"
                size="lg"
                data-testid="hero-host-btn"
                aria-label={HOST_TYPEWRITER_ARIA_LABEL}
                onClick={() => nav(hostCta.path)}
              >
                <HostTypewriterLabel />
                <span aria-hidden="true">→</span>
              </Button>
            </div>
            <p className="mt-5">
              <Link
                to="/for-organizers"
                className="font-ui font-medium text-[13px] text-white/70 underline underline-offset-4 decoration-white/25 hover:text-white/90 hover:decoration-white/50 transition-colors"
                data-testid="hero-for-organizers-link"
              >
                Hosting a scene? Tools for classes &amp; schools
              </Link>
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-page lg:max-w-5xl px-4 lg:px-8 pt-10 lg:pt-14 pb-4">
        <DemoLandingSection event={demoEvent} />
        <HomeFeatureMatrix onTryDemo={openDemoChooser} />
      </div>
    </HostThemePreviewShell>
  );
}
